import React, {useContext, useState, useEffect, useRef} from 'react';
import {Button, TextField, Typography} from '@mui/material';
import {ApplicationContext} from '../context/ApplicationContext';
import {verify2FA} from '../services/login.service';
import {CircularProgress} from "@mui/material";
import {useLocation} from 'react-router-dom';
import config from "../config";
import './Login.scss';

const PARTICLE_COLORS = [
    'rgba(99, 102, 241,',
    'rgba(139, 92, 246,',
    'rgba(167, 139, 250,',
    'rgba(79, 70, 229,',
];

const Verify2FAPage = (props) => {
    const AppContext = useContext(ApplicationContext);
    const location = useLocation();
    const [code, setCode] = useState('');
    const [isWaitingOnServer, setIsWaitingOnServer] = useState(false);
    const [username, setUsername] = useState('');
    const canvasRef = useRef(null);

    useEffect(() => {
        const stateUsername = location?.state?.username;
        const urlParams = new URLSearchParams(window.location.search);
        const queryUsername = urlParams.get('username');

        const user = stateUsername || queryUsername;
        if (!user) {
            window.location.replace(`${config.uiBaseUrl}/`);
            return;
        }
        setUsername(user);
    }, [location]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const particles = Array.from({length: 70}, () => ({
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            r: Math.random() * 2.5 + 0.5,
            speed: Math.random() * 0.6 + 0.25,
            opacity: Math.random() * 0.6 + 0.2,
            opacityDir: Math.random() > 0.5 ? 1 : -1,
            opacitySpeed: Math.random() * 0.006 + 0.002,
            color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
            drift: (Math.random() - 0.5) * 0.4,
        }));

        let animId;
        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach(p => {
                p.y -= p.speed;
                p.x += p.drift;
                p.opacity += p.opacityDir * p.opacitySpeed;
                if (p.opacity > 0.9 || p.opacity < 0.1) p.opacityDir *= -1;
                if (p.y < -10) {
                    p.y = canvas.height + 10;
                    p.x = Math.random() * canvas.width;
                }
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `${p.color}${p.opacity})`;
                ctx.shadowBlur = 8;
                ctx.shadowColor = `${p.color}0.9)`;
                ctx.fill();
                ctx.shadowBlur = 0;
            });
            animId = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', resize);
        };
    }, []);

    const submitForm = (username, code) => {
        setIsWaitingOnServer(true);
        verify2FA(username, code)
            .then(() => {
                window.location.replace(`${config.uiBaseUrl}/instantAnalytics`);
                setIsWaitingOnServer(false);
            })
            .catch((err) => {
                console.log(err);
                AppContext.handleError('Invalid verification code. Please try again.');
                setIsWaitingOnServer(false);
                setCode('');
            });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && code && code.length === 6 && !isWaitingOnServer) {
            submitForm(username, code);
        }
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setCode(value);
    };

    const background = (
        <>
            <canvas ref={canvasRef} className="login-particles-canvas" aria-hidden="true" />
            <div className="login-bg-orbs" aria-hidden="true">
                <div className="orb orb-1" />
                <div className="orb orb-2" />
                <div className="orb orb-3" />
                <div className="orb orb-4" />
            </div>
        </>
    );

    if (!username || isWaitingOnServer) {
        return (
            <div className="login-container">
                {background}
                <div className="login-card loading-state">
                    <CircularProgress size={48} style={{color: '#6366f1', marginBottom: '24px'}} />
                    <Typography variant="h6" className="primary-font" style={{fontWeight: 500}}>
                        {isWaitingOnServer ? 'Verifying...' : 'Loading...'}
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            {background}
            <div className="login-card">
                <div className="login-header">
                    <Typography
                        variant="h3"
                        className="login-title"
                        style={{fontFamily: 'Cookie, cursive', fontSize: '48px', marginBottom: '4px'}}
                    >
                        Verdantime
                    </Typography>
                    <div className="login-accent-bar" />
                    <Typography variant="body2" className="secondary-font" style={{marginTop: '16px', marginBottom: 0}}>
                        Enter the 6-digit code from your authenticator app
                    </Typography>
                </div>

                <div className="login-form">
                    <TextField
                        name="totp_code"
                        type="text"
                        inputMode="numeric"
                        onChange={handleCodeChange}
                        onKeyDown={handleKeyDown}
                        label="Verification Code"
                        variant="standard"
                        fullWidth
                        className="login-input"
                        autoComplete="one-time-code"
                        autoFocus
                        value={code}
                        inputProps={{
                            maxLength: 6,
                            style: {
                                textAlign: 'center',
                                fontSize: '24px',
                                letterSpacing: '10px',
                                fontFamily: 'monospace',
                                color: '#e8e8f4',
                            }
                        }}
                    />

                    <Button
                        className="login-button primary-font"
                        onClick={() => submitForm(username, code)}
                        disabled={isWaitingOnServer || !code || code.length !== 6}
                        variant="contained"
                        fullWidth
                        size="large"
                        style={{marginTop: '32px'}}
                    >
                        Verify
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default Verify2FAPage;
